/**
 * Asset Display Helpers
 * 
 * This file contains utilities for displaying crypto asset information
 * like logos, colors, and visual styling. These are purely presentational
 * helpers with no business logic.
 */

/**
 * getAssetLogo
 * 
 * PURPOSE: Get the emoji/symbol logo for a crypto asset
 * 
 * WHAT IT DOES:
 * - Returns a unique emoji for each supported cryptocurrency
 * - Falls back to first letter of symbol if asset not in map
 * 
 * WHEN IT'S USED:
 * - Race cards (asset identification)
 * - Race lanes (car badges)
 * - Winner history ticker
 * - Bet slips
 * 
 * @param symbol - Crypto symbol (e.g., 'BTC', 'SOL', 'ETH')
 * @returns Emoji logo or first letter as fallback
 */
export const getAssetLogo = (symbol: string): string => {
  const logoMap: { [key: string]: string } = {
    'BTC': '₿',      // Bitcoin
    'ETH': 'Ξ',      // Ethereum
    'SOL': '◎',      // Solana
    'XRP': '◊',      // Ripple
    'BNB': '⬢',      // Binance Coin
    'TRX': '⚡',      // Tron
    'DOGE': 'Ð',     // Dogecoin
    'ADA': '₳',      // Cardano
    'HYPE': '🔷',    // Hyperliquid
    'PEPE': '🐸',    // Pepe
    'DOT': '●',      // Polkadot
    'AVAX': '🔺',    // Avalanche
    'MATIC': '🔶',   // Polygon
    'LINK': '🔷',    // Chainlink
    'ATOM': '🔥',    // Cosmos
    'UNI': '⚪'      // Uniswap
  };
  return logoMap[symbol.toUpperCase()] || symbol.charAt(0);
};

/**
 * getAssetColor
 * 
 * PURPOSE: Get the colored emoji indicator for race history displays
 * 
 * WHAT IT DOES:
 * - Returns a colored circle emoji representing each crypto
 * - Used in scrolling winner history ticker
 * - Provides quick visual identification
 * 
 * WHEN IT'S USED:
 * - Race history ticker (scrolling winners)
 * - Quick visual asset identification
 * 
 * NOTE: These are emoji circles, not CSS colors. For CSS colors, use getAssetCSSColor()
 * 
 * @param symbol - Crypto symbol
 * @returns Colored circle emoji
 */
export const getAssetColor = (symbol: string): string => {
  const colorMap: { [key: string]: string } = {
    'BTC': '🟠',     // Orange
    'ETH': '🔵',     // Blue
    'SOL': '🟣',     // Purple
    'XRP': '⚪',     // White/Cyan
    'BNB': '🟡',     // Yellow
    'TRX': '🔴',     // Red
    'DOGE': '🟡',    // Yellow
    'ADA': '🔵',     // Blue
    'HYPE': '🔷',    // Blue Diamond
    'PEPE': '🟢',    // Green
    'DOT': '🟤',     // Brown
    'ATOM': '🔴',    // Red
    'UNI': '🟢',     // Green
    'LINK': '🔵',    // Blue
    'AVAX': '🔴',    // Red
    'MATIC': '🟣'    // Purple
  };
  return colorMap[symbol] || '⚪';
};

/**
 * getAssetCSSColor
 * 
 * PURPOSE: Get the CSS hex color for styling asset elements
 * 
 * WHAT IT DOES:
 * - Returns hex color codes for each crypto's brand color
 * - Used for borders, glows, text colors, and visual effects
 * - Matches each crypto's official/community brand colors
 * 
 * WHEN IT'S USED:
 * - Car colors and trails
 * - Lane borders and highlights
 * - Text colors and shadows
 * - Glow effects
 * - Performance indicators
 * 
 * COLOR CHOICES:
 * - Based on official brand colors where available
 * - Adjusted for visibility on dark backgrounds
 * - High contrast for racing visuals
 * 
 * @param symbol - Crypto symbol
 * @returns Hex color code (e.g., '#f7931a')
 */
export const getAssetCSSColor = (symbol: string): string => {
  const colorMap: { [key: string]: string } = {
    'BTC': '#f7931a',   // Bitcoin Orange (official)
    'ETH': '#627eea',   // Ethereum Blue
    'SOL': '#9945ff',   // Solana Purple (official)
    'XRP': '#00aae4',   // XRP Cyan (official)
    'BNB': '#f0b90b',   // Binance Yellow/Gold (official)
    'TRX': '#ef1c25',   // Tron Red (official)
    'DOGE': '#c2a633',  // Dogecoin Gold
    'ADA': '#0033ad',   // Cardano Blue (official)
    'HYPE': '#00d4ff',  // Hyperliquid Cyan/Blue
    'PEPE': '#3ba55c',  // Pepe Green (meme frog green)
    'DOT': '#8b4513',   // Polkadot Brown
    'ATOM': '#dc2626',  // Cosmos Red
    'UNI': '#16a34a',   // Uniswap Green
    'LINK': '#0077be',  // Chainlink Blue
    'AVAX': '#dc2626',  // Avalanche Red
    'MATIC': '#9d4edd'  // Polygon Purple
  };
  return colorMap[symbol] || '#ffffff';
};

/**
 * getAssetColorPalette
 * 
 * PURPOSE: Get a complete color palette for gradients and advanced styling
 * 
 * WHAT IT DOES:
 * - Returns primary, secondary (lighter), and dark shades
 * - Automatically generates secondary and dark variants from primary
 * - Used for gradients, button states, and progressive elements
 * 
 * WHEN IT'S USED:
 * - Game session controls (Start Game button gradients)
 * - Payout ladder displays
 * - Progressive UI elements that need multiple shades
 * 
 * @param symbol - Crypto symbol
 * @returns Object with primary, secondary (lighter), and dark color variants
 */
export const getAssetColorPalette = (symbol: string): { 
  primary: string; 
  secondary: string; 
  dark: string 
} => {
  const paletteMap: { [key: string]: { primary: string; secondary: string; dark: string } } = {
    'BTC': { primary: '#f7931a', secondary: '#EA9400', dark: '#BD4800' },
    'ETH': { primary: '#627eea', secondary: '#8A9FFF', dark: '#3D5FCC' },
    'SOL': { primary: '#9945ff', secondary: '#B874FF', dark: '#7030CC' },
    'XRP': { primary: '#00aae4', secondary: '#33BFEB', dark: '#0088B8' },
    'BNB': { primary: '#f0b90b', secondary: '#F3C84A', dark: '#C99508' },
    'TRX': { primary: '#ef1c25', secondary: '#FF4A50', dark: '#C00008' },
    'DOGE': { primary: '#c2a633', secondary: '#D9C15F', dark: '#9A8217' },
    'ADA': { primary: '#0033ad', secondary: '#2A5FD9', dark: '#001D6B' },
    'HYPE': { primary: '#00d4ff', secondary: '#33DFFF', dark: '#00A8CC' },
    'PEPE': { primary: '#3ba55c', secondary: '#5FC182', dark: '#2A7A44' },
    'DOT': { primary: '#e6007a', secondary: '#EB33A0', dark: '#B80060' },
    'ATOM': { primary: '#2e3148', secondary: '#5A5F85', dark: '#1A1D2E' },
    'UNI': { primary: '#ff007a', secondary: '#FF33A0', dark: '#CC0062' },
    'LINK': { primary: '#0077be', secondary: '#3396D1', dark: '#005F98' },
    'AVAX': { primary: '#e84142', secondary: '#ED6768', dark: '#BA3435' },
    'MATIC': { primary: '#8247e5', secondary: '#9D6BEA', dark: '#6839B7' }
  };
  
  return paletteMap[symbol] || { 
    primary: '#888888', 
    secondary: '#AAAAAA', 
    dark: '#555555' 
  };
};

/**
 * formatAssetPrice
 * 
 * PURPOSE: Smart price formatting for assets across different price ranges
 * 
 * WHAT IT DOES:
 * - Handles micro-cap tokens (PEPE, SHIB) with tiny prices like $0.00001234
 * - Handles mid-cap tokens ($0.50 - $999) with standard decimal places
 * - Handles large-cap tokens (BTC > $1000) with K notation
 * - Automatically adjusts precision based on price magnitude
 * 
 * WHEN IT'S USED:
 * - Race lane price displays
 * - Asset cards
 * - Any UI showing current crypto prices
 * 
 * @param price - Asset price in USD
 * @param isMobile - Whether to use mobile-optimized formatting (less decimals)
 * @returns Formatted price string
 */
export const formatAssetPrice = (price: number, isMobile: boolean = false): string => {
  if (price <= 0) {
    return isMobile ? '0.0000' : '0.00000';
  }
  
  // Large cap: $1000+ → use K notation (e.g., BTC = 95.23K)
  // Mobile: 2 decimals (95.23K), Desktop: 3 decimals (95.234K)
  if (price >= 1000) {
    return `${(price / 1000).toFixed(isMobile ? 2 : 3)}K`;
  }
  
  // Mid cap: $1 - $999 → standard decimals (e.g., SOL = 142.5678)
  // Mobile: 4 decimals (142.5678), Desktop: 5 decimals (142.56789)
  if (price >= 1) {
    return price.toFixed(isMobile ? 4 : 5);
  }
  
  // Small values: $0.01 - $0.99 → normal decimals
  // Mobile: 5 decimals (0.24567), Desktop: 6 decimals (0.245678)
  if (price >= 0.01) {
    return price.toFixed(isMobile ? 5 : 6);
  }
  
  // Micro values < $0.01 → use subscript notation for leading zeros
  // e.g., 0.00000488 becomes 0.0₅48856
  const priceStr = price.toFixed(20); // Get high precision
  const match = priceStr.match(/^0\.0+/); // Match "0." followed by zeros
  
  if (match) {
    const leadingZeros = match[0].length - 2; // Subtract "0."
    if (leadingZeros >= 2) {
      // Use subscript notation
      const subscripts = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉'];
      const subscriptNum = leadingZeros.toString().split('').map(d => subscripts[parseInt(d)]).join('');
      const remainingDigits = priceStr.slice(match[0].length).slice(0, isMobile ? 5 : 6);
      return `0.0${subscriptNum}${remainingDigits}`;
    }
  }
  
  // Fallback: normal decimal notation
  return price.toFixed(8);
};

/**
 * Asset Helper Summary
 * 
 * These five functions work together to provide consistent asset branding:
 * 
 * 1. getAssetLogo()        → Text/emoji representation (₿, Ξ, ◎)
 * 2. getAssetColor()       → Emoji color indicator (🟠, 🔵, 🟣)
 * 3. getAssetCSSColor()    → Single CSS hex color (#f7931a, #627eea, #9945ff)
 * 4. getAssetColorPalette() → Color palette with primary/secondary/dark shades
 * 5. formatAssetPrice()    → Smart price formatting for all price ranges
 * 
 * Example Usage:
 * ```typescript
 * const symbol = 'BTC';
 * 
 * // Display in text
 * <span>{getAssetLogo(symbol)}</span>  // Shows: ₿
 * 
 * // Show in history
 * <span>{getAssetColor(symbol)}</span>  // Shows: 🟠
 * 
 * // Single color styling
 * <div style={{ 
 *   color: getAssetCSSColor(symbol),           // Orange text (#f7931a)
 *   borderColor: getAssetCSSColor(symbol),     // Orange border
 *   boxShadow: `0 0 10px ${getAssetCSSColor(symbol)}` // Orange glow
 * }}>
 *   {symbol}
 * </div>
 * 
 * // Gradient/palette styling
 * const palette = getAssetColorPalette(symbol);
 * <button style={{
 *   background: `linear-gradient(135deg, ${palette.primary}, ${palette.secondary})`,
 *   borderColor: palette.dark
 * }}>
 *   Start Game
 * </button>
 * ```
 */

